/**
 * The state gate, and then the routes.
 *
 * Two layers, in this order, and the order is the point:
 *
 *   1. **The status** from `state.tsx`. Every `AppStatus` gets a screen of its
 *      own — signed out, second factor, outside every organisation, an
 *      unfinished deployment, an unreachable one. None of these is an error
 *      banner on a page that half-rendered.
 *   2. **The path**, once the status is `ready`. The player (`/course/:id/play`)
 *      is matched before the Shell wraps anything, because it is the one screen
 *      that is not inside the Shell: the course is the page.
 *
 * The Teach routes check `me.canTeach` here as well as on the server. Not as
 * security — the Worker refuses either way — but because a member who follows a
 * link to a report should be told there is nothing there rather than watch a
 * page load itself into a 403.
 */

import { useEffect } from 'react';
import type React from 'react';
import { Shell } from './components/Shell';
import { ds } from './ds';
import { matchRoute, useRouter } from './router';
import { AppProvider, useApp } from './state';
import { accountOrigin, display, mono, signInHref } from './theme';

import Home from './pages/Home';
import Learning from './pages/Learning';
import Bundles from './pages/Bundles';
import Bundle from './pages/Bundle';
import Catalogue from './pages/Catalogue';
import Course from './pages/Course';
import Play from './pages/Play';
import Done from './pages/Done';
import Search from './pages/Search';
import Notifications from './pages/Notifications';
import TeachCourses from './pages/TeachCourses';
import Reports from './pages/Reports';
import Report from './pages/Report';
import FeedbackForm from './pages/FeedbackForm';
import TeachBundles from './pages/TeachBundles';
import BundleEditor from './pages/BundleEditor';
import People from './pages/People';
import Guests from './pages/Guests';

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        padding: 24,
        background: 'var(--bg-page)',
        textAlign: 'center'
      }}
    >
      {children}
    </div>
  );
}

function SignedOut() {
  useEffect(() => {
    window.location.href = signInHref(window.location.href);
  }, []);
  return (
    <Centered>
      <p style={mono}>Taking you to sign in</p>
    </Centered>
  );
}

function SecondFactor() {
  const { Button } = ds();
  return (
    <Centered>
      <h1 style={display(28)}>Finish signing in.</h1>
      <p style={{ fontSize: 15, color: 'var(--text-muted)', maxWidth: 420 }}>
        Your account uses two-factor authentication.
      </p>
      <Button
        variant="primary"
        size="md"
        onClick={() => {
          // /signin would mint another session in the same condition and loop.
          const next = encodeURIComponent(window.location.href);
          window.location.href = `${new URL(signInHref('/')).origin}/signin/two-factor?next=${next}`;
        }}
      >
        Continue
      </Button>
    </Centered>
  );
}

function Outside() {
  const { Button } = ds();
  return (
    <Centered>
      <h1 style={display(28)}>Learn is part of Enterprise.</h1>
      <p style={{ fontSize: 15, color: 'var(--text-muted)', maxWidth: 420 }}>
        Ask your organisation's administrator to add you.
      </p>
      {/* The plans live on the main site, not on the enterprise console — the
          console is for organisations that already exist. */}
      <Button
        variant="primary"
        size="md"
        onClick={() => (window.location.href = `${accountOrigin()}/pricing`)}
      >
        See Enterprise
      </Button>
    </Centered>
  );
}

function Unreachable({ onRetry }: { onRetry: () => void }) {
  const { Button } = ds();
  return (
    <Centered>
      <h1 style={display(28)}>Learn is not answering.</h1>
      <Button variant="primary" size="md" onClick={onRetry}>
        Try again
      </Button>
    </Centered>
  );
}

function NotReady() {
  const { notReady } = useApp();
  return (
    <Centered>
      <h1 style={display(28)}>This deployment is not finished.</h1>
      <p style={{ fontSize: 15, color: 'var(--text-muted)', maxWidth: 520 }}>{notReady?.message}</p>
      {notReady?.missingTables?.length ? (
        <p style={{ ...mono, fontSize: 10 }}>Missing · {notReady.missingTables.join(' · ')}</p>
      ) : null}
      {notReady?.hint && (
        <p style={{ fontSize: 13.5, color: 'var(--text-faint)', maxWidth: 520 }}>{notReady.hint}</p>
      )}
    </Centered>
  );
}

function NotFound() {
  const { Button } = ds();
  const { navigate } = useRouter();
  return (
    <>
      <h1 style={display(28)}>Nothing here.</h1>
      <div>
        <Button variant="secondary" size="md" onClick={() => navigate('/')}>
          Go home
        </Button>
      </div>
    </>
  );
}

/** The signed-in routes, inside the Shell. */
function Page({ path, canTeach }: { path: string; canTeach: boolean }) {
  if (path === '/') return <Home />;
  if (path === '/learning') return <Learning />;
  if (path === '/bundles') return <Bundles />;
  if (path === '/catalogue') return <Catalogue />;
  if (path === '/search') return <Search />;
  if (path === '/notifications') return <Notifications />;

  const bundle = matchRoute('/bundle/:id', path);
  if (bundle?.id) return <Bundle id={bundle.id} />;

  const course = matchRoute('/course/:id', path);
  if (course?.id) return <Course id={course.id} />;

  const done = matchRoute('/course/:id/done', path);
  if (done?.id) return <Done id={done.id} />;

  if (canTeach) {
    if (path === '/teach/courses') return <TeachCourses />;
    if (path === '/teach/reports') return <Reports />;
    if (path === '/teach/bundles') return <TeachBundles />;
    if (path === '/teach/people') return <People />;
    if (path === '/teach/guests') return <Guests />;

    const report = matchRoute('/teach/courses/:id/report', path);
    if (report?.id) return <Report id={report.id} />;

    const form = matchRoute('/teach/courses/:id/feedback', path);
    if (form?.id) return <FeedbackForm id={form.id} />;

    const editor = matchRoute('/teach/bundles/:id', path);
    if (editor?.id) return <BundleEditor id={editor.id} />;
  }

  return <NotFound />;
}

function Routes() {
  const { status, refresh, me } = useApp();
  const { path } = useRouter();

  if (status === 'loading') {
    return (
      <Centered>
        <p style={mono}>Loading</p>
      </Centered>
    );
  }
  if (status === 'signedOut') return <SignedOut />;
  if (status === 'secondFactor') return <SecondFactor />;
  if (status === 'outside') return <Outside />;
  if (status === 'notReady') return <NotReady />;
  if (status === 'unreachable') return <Unreachable onRetry={() => void refresh()} />;

  // Before the Shell: the course fills the window.
  const play = matchRoute('/course/:id/play', path);
  if (play?.id) return <Play id={play.id} />;

  return (
    <Shell>
      <Page path={path} canTeach={!!me?.canTeach} />
    </Shell>
  );
}

export function App() {
  return (
    <AppProvider>
      <Routes />
    </AppProvider>
  );
}
